import * as React from 'react';
import { cn } from '@ais-ui/utils';
import { AccordionItem, AccordionTrigger } from './Accordion';

/**
 * AccordionListItem - A reusable component for list items with kebab menus
 *
 * This component provides a consistent layout for accordion list items that need:
 * - Action buttons on the left (show/hide, enable/disable, etc.)
 * - Text content with truncation
 * - A kebab menu (dropdown) always aligned on the right
 *
 * Uses CSS Grid to ensure kebab menus are always visible and vertically aligned
 * across all items, regardless of text length.
 *
 * @example
 * ```tsx
 * <AccordionListItem
 *   value={item.id}
 *   actionButtons={
 *     <>
 *       <ApplyButton item={item} />
 *       <ShowButton item={item} />
 *       <Separator orientation="vertical" className="h-6 bg-border" />
 *     </>
 *   }
 *   triggerContent={
 *     <>
 *       <ItemIcon className="size-4 flex-shrink-0" />
 *       <TruncatedText text={item.name} maxWidth="180px" />
 *     </>
 *   }
 *   kebabMenu={
 *     <DropdownMenu>
 *       <DropdownMenuTrigger asChild>
 *         <Button size="icon" variant="ghost" className="size-7">
 *           <PiDotsThreeVertical className="size-5" />
 *         </Button>
 *       </DropdownMenuTrigger>
 *       <DropdownMenuContent>...</DropdownMenuContent>
 *     </DropdownMenu>
 *   }
 * >
 *   <AccordionContent>
 *     // Your item details here
 *   </AccordionContent>
 * </AccordionListItem>
 * ```
 */

interface AccordionListItemProps {
  /** Unique value for the accordion item (typically an ID) */
  value: string;
  /** Action buttons displayed on the left (show/hide, enable/disable, etc.) */
  actionButtons: React.ReactNode;
  /** Content for the accordion trigger (icon, text, badges, etc.) */
  triggerContent: React.ReactNode;
  /** Kebab menu (dropdown) displayed on the right, always visible and aligned */
  kebabMenu: React.ReactNode;
  /** Content shown when accordion is expanded */
  children: React.ReactNode;
  /** Additional className for the AccordionItem */
  className?: string;
  /** HTML id attribute for E2E testing and accessibility */
  id?: string;
  /** Width reserved for kebab menu column (default: 56px) */
  kebabMenuWidth?: number;
  /** Use compact padding (py-2 instead of py-4) for items with multi-line content */
  compact?: boolean;
}

export const AccordionListItem = React.forwardRef<
  HTMLDivElement,
  AccordionListItemProps
>(
  (
    {
      value,
      actionButtons,
      triggerContent,
      kebabMenu,
      children,
      className,
      id,
      kebabMenuWidth = 34,
      compact = false,
    },
    ref,
  ) => {
    return (
      <AccordionItem
        ref={ref}
        value={value}
        className={cn('pr-2 pl-2', className)}
        id={id}
      >
        {/*
          Grid layout with 2 columns:
          - Column 1 (1fr): Takes all available space for buttons + trigger + text
          - Column 2 (fixed width): Reserved space for kebab menu - always visible and aligned
        */}
        <div
          className={cn('grid items-center w-full pr-2')}
          style={{
            gridTemplateColumns: `1fr ${kebabMenuWidth}px`,
          }}
        >
          {/* Left column: Action buttons + Accordion trigger */}
          <div className="flex items-center min-w-0">
            {/* Action buttons (fixed size) */}
            <div className="flex items-center flex-shrink-0">
              {actionButtons}
            </div>

            {/* Accordion trigger (flex-1 to fill remaining space) */}
            <AccordionTrigger
              className={cn('min-w-0 flex-1', compact ? 'py-2' : 'py-4')}
              hideChevron={true}
            >
              <div className="flex space-x-2 items-center ml-4 min-w-0 flex-1">
                {triggerContent}
              </div>
            </AccordionTrigger>
          </div>

          {/* Right column: Kebab menu (always at fixed position) */}
          <div className="flex justify-end items-center">{kebabMenu}</div>
        </div>

        {/* Accordion content */}
        {children}
      </AccordionItem>
    );
  },
);

AccordionListItem.displayName = 'AccordionListItem';
